import React, { useState, useEffect } from "react";
import {
    Box,
    BottomNavigation,
    BottomNavigationAction,
    Typography,
    Divider,
    Badge,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    SwipeableDrawer,
    IconButton,
    Tooltip,
    Chip,
    List,
    ListItemIcon,
    ListItemText,
    ListItemButton,
} from "@mui/material";
import {
    Storefront as ShopIcon,
    Dashboard as DashboardIcon,
    AddTask as AddTaskIcon,
    ArrowForward as ArrowIcon,
    ChildCare as ChildIcon,
    DarkMode as DarkIcon,
    LightMode as LightIcon,
    SettingsBrightness as SettingsBrightnessIcon,
    CheckCircleOutlineOutlined as ChoresIcon,
    Settings as SettingsIcon,
    Person as PersonIcon,
    CalendarMonth as CalendarIcon,
    CalendarToday as CalendarTodayIcon,
    Logout as LogoutIcon,
    Star as StarIcon,
    People as PeopleIcon,
    DeleteSweep as DeleteSweepIcon,
    Add as AddIcon,
} from "@mui/icons-material";
import { Fab } from "@mui/material";
import { advanceDevDate } from "../../utils/dateUtils";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useThemeMode } from "../../contexts/ThemeContext";
import { useEffectiveDark } from "../../hooks/useEffectiveDark";
import { useToast } from "../../contexts/ToastContext";
import { AnimatePresence, motion } from "framer-motion";
import api from "../../services/api";
import UserAvatar from "./UserAvatar";
import NotificationList from "./NotificationList";
import ApprovalList from "./ApprovalList";
import useNotifications from "../../hooks/useNotifications";
import useBadges from "../../hooks/useBadges";
import { getAccent, getBg, getEffectiveDark, getTextColor } from "../../theme/themes";
import { useAppSettings } from "../../contexts/AppSettingsContext";
import { getCategoryLabel } from "../../utils/categories";
import TaskFormDrawer from "./TaskFormDrawer";

const PinDot = ({ filled, accent, isDark }) => (
    <Box
        sx={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            bgcolor: filled ? accent : "transparent",
            border: `2px solid ${filled ? accent : isDark ? '#666' : '#ccc'}`,
            transition: "background 0.15s",
        }}
    />
);

// Child PIN login dialog
const ChildPinDialog = ({ open, onClose, onSuccess, isKiosk, isDark, isAdmin = false }) => {
    const toast = useToast();
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [pin, setPin] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            api.get("/auth/children")
                .then((r) => setChildren(r.data))
                .catch(() => {
                    toast.error("Lataus epäonnistui");
                    setError("Lataus epäonnistui");
                })
                .finally(() => setLoading(false));
        }
    }, [open, toast]);

    // Admin can skip PIN and elevate directly
    const handleAdminElevate = async (child) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const res = await api.post('/auth/admin-elevate', { childId: child.id });
            onSuccess(res.data.user, res.data.token);
            handleClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Elevation failed');
            toast.error('Elevation failed');
            setSubmitting(false);
        }
    };

    const handleChildClick = (child) => {
        if (isAdmin) {
            handleAdminElevate(child);
        } else {
            setSelected(child);
            setPin("");
            setError("");
        }
    };

    const handleDigit = async (d) => {
        if (submitting) return;
        const next = pin + d;
        setPin(next);
        setError("");
        if (next.length === 4) {
            setSubmitting(true);
            try {
                const res = await api.post("/auth/pin-login", {
                    userId: selected.id,
                    pin: next,
                });
                if (!isKiosk) {
                    localStorage.setItem("auth_token", res.data.token);
                    api.setAuthToken(res.data.token);
                }
                onSuccess(res.data.user, res.data.token);
            } catch (err) {
                const errorMsg = err.response?.data?.error || "Väärä PIN";
                setError(errorMsg);
                toast.error(errorMsg);
                setPin("");
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleClose = () => {
        setSelected(null);
        setPin("");
        setError("");
        setSubmitting(false);
        onClose();
    };

    const accent = selected ? getAccent(selected) : getAccent(null);
    const effectiveDark = getEffectiveDark(selected, isDark);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            slotProps={{ paper: { sx: { borderRadius: "24px", p: 1, bgcolor: isDark ? 'background.default' : '#fff' } } }}
        >
            <DialogTitle sx={{ textAlign: "center", fontWeight: 900, pb: 0, color: isDark ? '#fff' : 'text.primary' }}>
                {selected ? `Hei, ${selected.name}!` : "Valitse käyttäjä"}
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>Ladataan...</Box>
                ) : !selected ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            justifyContent: "center",
                            pt: 2,
                        }}
                    >
                        {children.map((child) => (
                            <Box
                                key={child.id}
                                onClick={() => handleChildClick(child)}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 0.75,
                                    cursor: "pointer",
                                    p: 1.5,
                                    borderRadius: "16px",
                                    bgcolor: getBg(child, isDark),
                                    transition: "transform 0.15s",
                                    "&:hover": { transform: "scale(1.05)" },
                                    minWidth: 80,
                                }}
                            >
                                <UserAvatar
                                    src={child.avatar_url}
                                    frame={child.avatar_frame}
                                    size={56}
                                    bgcolor={getAccent(child)}
                                    sx={{ fontWeight: 900, fontSize: "1.4rem" }}
                                >
                                    {child.name.charAt(0)}
                                </UserAvatar>
                                <Typography variant="body2" fontWeight={700} sx={{ color: getTextColor(child, isDark) }}>
                                    {child.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                            pt: 2,
                        }}
                    >
                        <Typography variant="body2" color={error ? "error" : "text.secondary"} role={error ? "alert" : undefined}>
                            {error || "Syötä PIN-koodi"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            {[0, 1, 2, 3].map((i) => (
                                <PinDot
                                    key={i}
                                    filled={i < pin.length}
                                    accent={accent}
                                    isDark={effectiveDark}
                                />
                            ))}
                        </Box>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3, 1fr)",
                                gap: 1,
                            }}
                        >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                                <Box
                                    key={d}
                                    onClick={() => handleDigit(String(d))}
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: "50%",
                                        bgcolor: effectiveDark ? 'background.paper' : '#f5f5f5',
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        fontSize: "1.3rem",
                                        fontWeight: 700,
                                        color: effectiveDark ? '#fff' : 'text.primary',
                                        "&:active": { bgcolor: `${accent}22` },
                                        userSelect: "none",
                                    }}
                                >
                                    {d}
                                </Box>
                            ))}
                            <Box
                                onClick={() => setSelected(null)}
                                sx={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                    color: 'text.secondary',
                                    userSelect: "none",
                                    "&:active": { bgcolor: effectiveDark ? 'background.paper' : '#f5f5f5' },
                                }}
                            >
                                ←
                            </Box>
                            <Box
                                onClick={() => handleDigit("0")}
                                sx={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: "50%",
                                    bgcolor: effectiveDark ? 'background.paper' : '#f5f5f5',
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    fontSize: "1.3rem",
                                    fontWeight: 700,
                                    color: effectiveDark ? '#fff' : 'text.primary',
                                    "&:active": { bgcolor: `${accent}22` },
                                    userSelect: "none",
                                }}
                            >
                                0
                            </Box>
                            <Box
                                onClick={() => setPin((p) => p.slice(0, -1))}
                                sx={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                    color: 'text.secondary',
                                    userSelect: "none",
                                    "&:active": { bgcolor: effectiveDark ? 'background.paper' : '#f5f5f5' },
                                }}
                            >
                                ⌫
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary" }}>
                    Peruuta
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Parent login dialog
const ParentLoginDialog = ({ open, onClose, onSuccess, isDark }) => {
    const toast = useToast();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [demoHints, setDemoHints] = useState(null);
    const { login } = useAuth();

    // Fetch demo hints when dialog opens
    useEffect(() => {
        if (open) {
            api.getSetupStatus().then(res => {
                const hints = res.data.demoHints || null;
                setDemoHints(hints);
                if (hints?.parentCredentials) {
                    const [u, p] = hints.parentCredentials.split(' / ');
                    if (u) setUsername(u);
                    if (p) setPassword(p);
                }
            }).catch(() => {});
        }
    }, [open]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!username || !password) return;
        setSubmitting(true);
        const result = await login(username, password);
        setSubmitting(false);
        if (result.success) {
            onSuccess(result.user);
        } else {
            toast.error(result.error);
        }
    };

    const handleClose = () => {
        setUsername("");
        setPassword("");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            slotProps={{ paper: { sx: { borderRadius: "24px", p: 1, bgcolor: isDark ? 'background.default' : '#fff' } } }}
        >
            <DialogTitle sx={{ textAlign: "center", fontWeight: 900, pb: 0, color: isDark ? '#fff' : 'text.primary' }}>
                Vanhemman kirjautuminen
            </DialogTitle>
            <DialogContent>
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        pt: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <TextField
                        label="Käyttäjätunnus"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        fullWidth
                        autoFocus
                        disabled={submitting}
                    />
                    <TextField
                        label="Salasana"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        disabled={submitting}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={submitting || !username || !password}
                        sx={{
                            bgcolor: getAccent(null),
                            "&:hover": { bgcolor: getAccent(null) },
                            borderRadius: 50,
                            py: 1.5,
                            fontWeight: 700,
                        }}
                    >
                        {submitting ? "Kirjaudutaan..." : "Kirjaudu"}
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary" }}>
                    Peruuta
                </Button>
            </DialogActions>
            {demoHints && (
                <Box sx={{ px: 3, pb: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        Demo: {demoHints.parentCredentials} tai {demoHints.kioskCredentials}
                    </Typography>
                </Box>
            )}
        </Dialog>
    );
};

const BOTTOM_NAV = [
    { label: "Kalenteri", icon: <CalendarIcon />, path: "/kalenteri" },
    { label: "Kauppa", icon: <ShopIcon />, path: "/kauppa" },
    { label: "Tehtävät", icon: <ChoresIcon />, path: "/" },
    { label: "Profiili", icon: <PersonIcon />, path: "/profiili" },
    { label: "Tili", icon: <PersonIcon />, path: "/profiili", isMenu: true },
];

const MobileNav = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [childLoginOpen, setChildLoginOpen] = useState(false);
    const [parentLoginOpen, setParentLoginOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const {
        user,
        isKiosk,
        isElevated,
        elevatedChild,
        elevateChild,
        deElevateChild,
        demoModeEnabled,
        logout,
    } = useAuth();
    const { mode, preference, setThemePreference } = useThemeMode();
    const effectiveDark = useEffectiveDark();
    const { notifications, pendingApprovals, dismissNotification, clearAllNotifications, handleApprove: approveItem, handleReject: rejectItem } = useNotifications({ user, elevatedChild, isKiosk });
    const { shopBadge, achievementsBadge } = useBadges(user?.id);
    const { features } = useAppSettings();

    // Combined task count (open tasks + bonus) for badge on bottom nav
    const [taskBadgeCount, setTaskBadgeCount] = useState(0);
    // News badge
    const [newsBadge, setNewsBadge] = useState(false);
    const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
    const [taskDrawerEditTask, setTaskDrawerEditTask] = useState(null);

    // Listen for external open-task-drawer events (from TaskManager, etc.)
    useEffect(() => {
        const handler = (e) => {
            setTaskDrawerEditTask(e.detail?.task || null);
            setTaskDrawerOpen(true);
        };
        window.addEventListener('open-task-drawer', handler);
        return () => window.removeEventListener('open-task-drawer', handler);
    }, []);

    const isChild = user?.type === "child";
    const isParent = user?.type === "parent";
    const showAdminItems = isParent && !isKiosk;
    const showChildrenButton = isParent || isKiosk;

    const accent = getAccent(user);

    // Map current path to bottom nav index
    const bottomIdx = BOTTOM_NAV.findIndex((n) => {
        if (n.isMenu) return false;
        return n.path === location.pathname;
    });

    // Fetch combined task count (open tasks + bonus) for bottom nav badge (child users only)
    useEffect(() => {
        if (!isChild && !isElevated) { setTaskBadgeCount(0); return; }
        const fetchTaskCount = async () => {
            try {
                const uid = isElevated ? elevatedChild?.id : user?.id;
                if (!uid) return;
                const [bonusRes, todayRes] = await Promise.all([
                    api.get(`/bonus-tasks?userId=${uid}`),
                    api.get('/kiosk/today'),
                ]);
                const bonusCount = bonusRes.data?.length || 0;
                const childData = todayRes.data?.find(c => c.id === uid);
                const openTasks = childData?.tasks?.filter(t => !t.completed)?.length || 0;
                setTaskBadgeCount(openTasks + bonusCount);
            } catch { setTaskBadgeCount(0); }
        };
        fetchTaskCount();
        const interval = setInterval(fetchTaskCount, 30000);
        return () => clearInterval(interval);
    }, [isChild, isElevated, elevatedChild, user]);

    // News badge — check rotation
    useEffect(() => {
        const checkNewsBadge = async () => {
            try {
                const res = await api.get('/shop/news');
                const activeWeek = res.data?.activeWeek;
                if (activeWeek?.id) {
                    const lastSeen = localStorage.getItem('lastSeenRotationId');
                    setNewsBadge(lastSeen !== String(activeWeek.id));
                }
            } catch { /* ignore */ }
        };
        checkNewsBadge();
    }, []);

    const handleBottomNav = (_, idx) => {
        const item = BOTTOM_NAV[idx];
        if (item.isMenu) return;
        navigate(item.path);
        handleDrawerClose();
    };

    const handleDrawerOpen = () => setDrawerOpen(prev => !prev);
    const handleDrawerClose = () => setDrawerOpen(false);

    const handleDrawerNav = (path) => {
        navigate(path);
        handleDrawerClose();
    };

    return (
        <>
            {/* Login Dialogs */}
            <ChildPinDialog
                open={childLoginOpen}
                onClose={() => setChildLoginOpen(false)}
                isKiosk={isKiosk}
                isDark={effectiveDark}
                isAdmin={isParent && !isKiosk}
                onSuccess={(childUser, token) => {
                    setChildLoginOpen(false);
                    if (isKiosk || (isParent && !isKiosk)) {
                        elevateChild(childUser, token);
                    } else {
                        window.location.reload();
                    }
                }}
            />
            <ParentLoginDialog
                open={parentLoginOpen}
                onClose={() => setParentLoginOpen(false)}
                isDark={effectiveDark}
                onSuccess={() => {
                    setParentLoginOpen(false);
                    navigate("/hallinta");
                }}
            />

            {/* Account Drawer */}
            <SwipeableDrawer
                anchor="right"
                open={drawerOpen}
                onClose={handleDrawerClose}
                onOpen={handleDrawerOpen}
                slotProps={{ paper: {
                    sx: {
                        bgcolor: effectiveDark ? "background.default" : "background.paper",
                        borderRadius: '0 0 0 16px',
                    },
                } }}
                ModalProps={{ sx: { zIndex: 1200 } }}
            >
              <Box
                sx={{
                  width: '65vw',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  pt: 'env(safe-area-inset-top, 0px)',
                  pb: 'calc(56px + env(safe-area-inset-bottom, 0px) + 16px)',
                  boxSizing: 'border-box',
                }}
              >
                {/* User info header */}
                {(user || elevatedChild) && (
                    <Box sx={{ px: 2, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                        <UserAvatar
                            src={(elevatedChild || user)?.avatar_url}
                            frame={(elevatedChild || user)?.avatar_frame}
                            size={44}
                            bgcolor={accent}
                            sx={{ fontWeight: 700 }}
                        >
                            {(elevatedChild || user)?.name?.charAt(0)}
                        </UserAvatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={700}>
                                {(elevatedChild || user)?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {isElevated ? "Korotettu" : getCategoryLabel(user)}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => {
                                if (preference === "light") setThemePreference("dark");
                                else if (preference === "dark") setThemePreference("system");
                                else setThemePreference("light");
                            }}
                            sx={{ color: accent }}
                        >
                            {preference === "system" ? (
                                <SettingsBrightnessIcon sx={{ fontSize: 20 }} />
                            ) : mode === "dark" ? (
                                <LightIcon sx={{ fontSize: 20 }} />
                            ) : (
                                <DarkIcon sx={{ fontSize: 20 }} />
                            )}
                        </IconButton>
                    </Box>
                )}
                <Divider />

                {/* Notifications stream */}
                <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="overline" color="text.secondary" fontWeight={700}>
                        ILMOITUKSET
                    </Typography>
                    {notifications.length > 0 && (
                        <Badge badgeContent={notifications.length} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }} />
                    )}
                    {notifications.length > 0 && (
                        <Tooltip title="Tyhjennä kaikki">
                            <IconButton size="small" onClick={clearAllNotifications} sx={{ ml: 'auto' }}>
                                <DeleteSweepIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Box sx={{ px: 1, maxHeight: showAdminItems ? 200 : undefined, flex: showAdminItems ? '0 0 auto' : 1, overflowY: 'auto', minHeight: 48 }}>
                    <NotificationList notifications={notifications} onDismiss={dismissNotification} accent={accent} />
                </Box>

                {/* Pending approvals for parents */}
                {showAdminItems && pendingApprovals.length > 0 && (
                    <>
                        <Divider sx={{ mt: 1 }} />
                        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                            <Typography variant="overline" color="text.secondary" fontWeight={700}>
                                Hyväksynnät ({pendingApprovals.length})
                            </Typography>
                        </Box>
                        <Box sx={{ px: 1, flex: '0 0 auto', overflowY: 'auto' }}>
                            <ApprovalList
                                approvals={pendingApprovals}
                                onApprove={approveItem}
                                onReject={rejectItem}
                                maxItems={2}
                                onShowMore={() => handleDrawerNav('/hallinta')}
                                accent={accent}
                            />
                        </Box>
                    </>
                )}

                {/* Bottom actions */}
                <Box sx={{ mt: 'auto' }}>
                    <Divider />
                    <List sx={{ py: 0.5, '& .MuiListItemButton-root': { flexDirection: 'row-reverse', py: 1.5 }, '& .MuiListItemIcon-root': { minWidth: 32, justifyContent: 'center' }, '& .MuiListItemText-root': { textAlign: 'right' } }}>
                        {/* Uutuudet */}
                        {features.uutiset && (
                        <ListItemButton onClick={() => handleDrawerNav("/uutiset")}>
                            <ListItemIcon><StarIcon sx={{ color: accent }} /></ListItemIcon>
                            <ListItemText primary="Uutuudet" />
                            {newsBadge && (
                                <Badge color="error" variant="dot" />
                            )}
                        </ListItemButton>
                        )}

                        {/* Child: Asetukset */}
                        {isChild && !isElevated && (
                            <ListItemButton onClick={() => handleDrawerNav("/asetukset")}>
                                <ListItemIcon><SettingsIcon sx={{ color: accent }} /></ListItemIcon>
                                <ListItemText primary="Asetukset" />
                            </ListItemButton>
                        )}

                        {/* Parent: Asetukset */}
                        {showAdminItems && !isElevated && (
                            <ListItemButton onClick={() => handleDrawerNav("/asetukset")}>
                                <ListItemIcon><SettingsIcon sx={{ color: accent }} /></ListItemIcon>
                                <ListItemText primary="Asetukset" />
                            </ListItemButton>
                        )}

                        {/* Kiosk: Asetukset + Logout */}
                        {isKiosk && !isElevated && (
                            <>
                            <ListItemButton onClick={() => handleDrawerNav("/asetukset")}>
                                <ListItemIcon><SettingsIcon sx={{ color: accent }} /></ListItemIcon>
                                <ListItemText primary="Asetukset" />
                            </ListItemButton>
                            <ListItemButton onClick={() => { logout(); handleDrawerClose(); }}>
                                <ListItemIcon><LogoutIcon sx={{ color: accent }} /></ListItemIcon>
                                <ListItemText primary="Kirjaudu ulos" />
                            </ListItemButton>
                            </>
                        )}

                        {/* Parent: Käyttäjät button */}
                        {!isElevated && showChildrenButton && (
                            <ListItemButton onClick={() => { setChildLoginOpen(true); handleDrawerClose(); }}>
                                <ListItemIcon><ChildIcon sx={{ color: accent }} /></ListItemIcon>
                                <ListItemText primary="Käyttäjät" />
                            </ListItemButton>
                        )}

                        {/* Login options when not logged in */}
                        {!isElevated && !user && (
                            <>
                                <ListItemButton onClick={() => { setChildLoginOpen(true); handleDrawerClose(); }}>
                                    <ListItemIcon><ChildIcon sx={{ color: accent }} /></ListItemIcon>
                                    <ListItemText primary="Käyttäjät — kirjaudu" />
                                </ListItemButton>
                                <ListItemButton onClick={() => { setParentLoginOpen(true); handleDrawerClose(); }}>
                                    <ListItemIcon><ArrowIcon sx={{ color: accent }} /></ListItemIcon>
                                    <ListItemText primary="Vanhemmat" />
                                </ListItemButton>
                            </>
                        )}

                        {/* Logout for elevated child */}
                        {isElevated && elevatedChild && (
                            <ListItemButton onClick={() => { deElevateChild(); handleDrawerClose(); }}>
                                <ListItemIcon><LogoutIcon sx={{ color: accent }} /></ListItemIcon>
                                <ListItemText primary="Kirjaudu ulos" />
                            </ListItemButton>
                        )}
                    </List>
                </Box>
              </Box>
            </SwipeableDrawer>

            {/* Admin floating chip bar */}
            <AnimatePresence>
            {showAdminItems && !isElevated && !['/kauppa', '/hallinta', '/tehtavat'].includes(location.pathname) && (
                <motion.div
                    key="admin-chips"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 16 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    style={{
                        position: 'fixed',
                        bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
                        left: 0,
                        right: 0,
                        zIndex: 1199,
                        pointerEvents: 'none',
                    }}
                >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1,
                        py: 1.5,
                        px: 2,
                        pointerEvents: 'none',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -24,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: effectiveDark
                                ? 'linear-gradient(0deg, rgba(0, 0, 0, 0.81) 0%, rgba(0, 0, 0, 0) 71%)'
                                : 'linear-gradient(to top, rgba(255, 255, 255, 0.53) 0%, rgba(255, 255, 255, 0) 70%)',
                            zIndex: -1,
                            pointerEvents: 'none',
                        },
                    }}
                >
                    {[
                        { label: 'Hallinta', icon: <DashboardIcon sx={{ fontSize: 16 }} />, path: '/hallinta' },
                        { label: 'Tehtävät', icon: <AddTaskIcon sx={{ fontSize: 16 }} />, path: '/tehtavat' },
                        { label: 'Lisää', icon: <AddIcon sx={{ fontSize: 16 }} />, action: 'openTaskDrawer' },
                        { label: 'Käyttäjät', icon: <PeopleIcon sx={{ fontSize: 16 }} />, path: null, action: 'childLogin' },
                    ].filter(c => !c.path || c.path.split('?')[0] !== location.pathname).map(chip => (
                        <Chip
                            key={chip.label}
                            icon={chip.icon}
                            label={chip.label}
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                if (chip.action === 'childLogin') {
                                    setChildLoginOpen(true);
                                } else if (chip.action === 'openTaskDrawer') {
                                    setTaskDrawerOpen(true);
                                } else {
                                    navigate(chip.path);
                                }
                            }}
                            sx={{
                                pointerEvents: 'auto',
                                borderRadius: 50,
                                fontWeight: 700,
                                minHeight: 32,
                                px: 0.5,
                                bgcolor: 'background.paper',
                                color: 'text.primary',
                                borderColor: 'divider',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                '&:hover': { bgcolor: `${accent} !important`, color: '#fff' },
                                '& .MuiChip-icon': { color: 'text.secondary', ml: 0.5 },
                            }}
                        />
                    ))}
                </Box>
                </motion.div>
            )}
            </AnimatePresence>

            {/* Bottom Navigation */}
            <Paper
                elevation={0}
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1300,
                    borderTop: `1px solid ${accent}22`,
                    paddingBottom: "env(safe-area-inset-bottom, 0px)",
                }}
            >
                <BottomNavigation
                    value={bottomIdx >= 0 ? bottomIdx : false}
                    onChange={handleBottomNav}
                    sx={{
                        bgcolor: "background.sidebar",
                        "& .MuiBottomNavigationAction-root": {
                            color: "#aaa",
                            minWidth: 0,
                            px: 0.5,
                        },
                        "& .Mui-selected": { color: accent },
                        "& .MuiBottomNavigationAction-label": {
                            fontSize: "0.6rem",
                            fontWeight: 600,
                        },
                    }}
                >
                    {BOTTOM_NAV.map(({ label, icon, path, isMenu }, idx) => (
                        isMenu ? (
                            <BottomNavigationAction
                                key={label}
                                label={label}
                                icon={
                                    (showAdminItems && pendingApprovals.length > 0) || notifications.length > 0 ? (
                                        <Badge
                                            badgeContent={showAdminItems ? pendingApprovals.length : notifications.length}
                                            color="error"
                                            sx={{
                                                "& .MuiBadge-badge": {
                                                    fontSize: "0.6rem",
                                                    height: 16,
                                                    minWidth: 16,
                                                },
                                            }}
                                        >
                                            <UserAvatar
                                                src={(elevatedChild || user)?.avatar_url}
                                                frame={(elevatedChild || user)?.avatar_frame}
                                                size={28}
                                                sx={{
                                                    bgcolor: accent,
                                                    fontSize: "0.8rem",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {(elevatedChild || user)?.name?.charAt(0) || <PersonIcon sx={{ fontSize: 18 }} />}
                                            </UserAvatar>
                                        </Badge>
                                    ) : (
                                        <UserAvatar
                                            src={(elevatedChild || user)?.avatar_url}
                                            frame={(elevatedChild || user)?.avatar_frame}
                                            size={28}
                                            sx={{
                                                bgcolor: accent,
                                                fontSize: "0.8rem",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {(elevatedChild || user)?.name?.charAt(0) || <PersonIcon sx={{ fontSize: 18 }} />}
                                        </UserAvatar>
                                    )
                                }
                                onClick={handleDrawerOpen}
                            />
                        ) : (
                            <BottomNavigationAction
                                key={label}
                                label={label}
                                icon={
                                    label === "Tehtävät" && taskBadgeCount > 0 && !isKiosk ? (
                                        <Badge
                                            badgeContent={taskBadgeCount}
                                            color="error"
                                            sx={{
                                                "& .MuiBadge-badge": {
                                                    fontSize: "0.6rem",
                                                    height: 16,
                                                    minWidth: 16,
                                                },
                                            }}
                                        >
                                            {icon}
                                        </Badge>
                                    ) : (path === '/kauppa' && shopBadge) || (path === '/profiili' && achievementsBadge) ? (
                                        <Box sx={{ position: 'relative', display: 'flex' }}>
                                            {icon}
                                            <Box sx={{
                                                position: 'absolute', top: -2, right: -4,
                                                width: 9, height: 9, borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #ff9800 0%, #ff5722 100%)',
                                                boxShadow: '0 2px 6px rgba(255,152,0,0.5)',
                                            }} />
                                        </Box>
                                    ) : (
                                        icon
                                    )
                                }
                            />
                        )
                    ))}
                </BottomNavigation>
            </Paper>

            {/* Demo mode: floating +1 day button — only on KioskDashboard */}
            {demoModeEnabled && location.pathname === '/' && (
                <Fab
                    onClick={() => { advanceDevDate(1); window.location.reload(); }}
                    sx={{
                        position: 'fixed',
                        bottom: 72,
                        left: 16,
                        bgcolor: '#000',
                        border: '2px solid #d32f2f',
                        color: '#FFD600',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        width: 56,
                        height: 56,
                        zIndex: 1300,
                        '&:hover': { bgcolor: '#1a1a1a' },
                        flexDirection: 'column',
                        gap: 0,
                    }}
                >
                    <CalendarTodayIcon sx={{ fontSize: 18, color: '#FFD600' }} />
                    <span style={{ fontSize: '0.6rem', lineHeight: 1, color: '#FFD600', fontWeight: 700 }}>+1pv</span>
                </Fab>
            )}

            <TaskFormDrawer
                open={taskDrawerOpen}
                onClose={() => { setTaskDrawerOpen(false); setTaskDrawerEditTask(null); }}
                onSuccess={() => window.dispatchEvent(new Event('task-drawer-success'))}
                editTask={taskDrawerEditTask}
                anchor="right"
            />
        </>
    );
};

export default MobileNav;
